import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  Row,
  RowSelectionState,
  useReactTable,
} from '@tanstack/react-table'
import Button from 'components/core/button'
import { useEffect, useState } from 'react'
import { Skeleton } from 'lib/components/skeleton/skeleton'
import { useInView } from 'react-intersection-observer'
import { toast } from 'sonner'
import SearchInput from 'lib/components/search-input/search-input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'lib/components/table/table'

interface EntityTableProps<T> {
  onFinished: (update?: boolean) => void
  handleAddDeliverables: (ticketIds: number[]) => void
  enableRowSelection?: boolean | ((row: Row<T>) => boolean)
  isChecked?: (row: Row<T>) => boolean
  buttonText: string
  selectedText?: string
  columns: ColumnDef<T>[]
  data: T[]
  isFetchingNextPage?: boolean
  fetchNextPage?: () => void
  error?: Error | null
  isLoading: boolean
  entitySingular: string
  entityPlural: string
}

const EntityTable = <T,>({
  onFinished,
  handleAddDeliverables,
  enableRowSelection,
  buttonText,
  selectedText,
  columns,
  data,
  isFetchingNextPage,
  fetchNextPage,
  error,
  isLoading,
  entitySingular,
  entityPlural,
}: EntityTableProps<T>) => {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const { ref, inView } = useInView()

  const [searchValue, setSearchValue] = useState('')

  const table = useReactTable({
    columns,
    data,
    state: {
      rowSelection,
      globalFilter: searchValue,
    },
    enableRowSelection: enableRowSelection,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: 'includesString',
  })

  const handleSubmit = () => {
    const ticketIds = Object.keys(rowSelection).map((key) => data[key].id)
    handleAddDeliverables(ticketIds)
  }

  useEffect(() => {
    if (error) {
      toast.error(`Failed to fetch ${entityPlural}`)
      console.error(`Failed to fetch ${entityPlural}`, error)
    }
  }, [error])

  useEffect(() => {
    if (inView && !isFetchingNextPage && fetchNextPage) {
      fetchNextPage()
    }
  }, [fetchNextPage, inView, isFetchingNextPage])

  return (
    <div>
      <div className="tw-mb-4 tw-mt-4 tw-flex tw-items-center tw-justify-between tw-gap-2">
        <SearchInput
          className="tw-flex-1"
          value={searchValue}
          onChange={setSearchValue}
          placeholder={`Search ${entityPlural}`}
        />
        <h5>
          {entityPlural} ({data?.length ?? 0})
        </h5>
      </div>
      <div>
        <Table>
          <TableHeader className="tw-sticky tw--top-4 tw-z-10">
            <TableRow intent="header">
              {table.getFlatHeaders().map((header) => (
                <TableHead key={header.id} style={{ width: `${header.getSize()}px` }}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id} intent="body" selected={row.getIsSelected()}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                ))}
              </TableRow>
            ))}
            <tr ref={ref} className="tw-h-4" />
          </TableBody>
        </Table>
        {(isLoading || isFetchingNextPage) && (
          <Skeleton className="tw-h-24 tw-w-full tw-bg-neutral-200" data-testid="skeleton" />
        )}
      </div>
      <div className="tw-sticky tw-bottom-0 tw-mt-4 tw-flex tw-w-full tw-justify-between tw-gap-2 tw-bg-white tw-py-4">
        <h5>
          {Object.keys(rowSelection).length}{' '}
          {selectedText || `${entitySingular}${Object.keys(rowSelection).length !== 1 ? 's' : ''} selected`}
        </h5>
        <div className="tw-flex tw-gap-2">
          <Button onClick={() => onFinished(false)} color="lightGray">
            Cancel
          </Button>
          <Button onClick={handleSubmit} color="purple" disabled={Object.keys(rowSelection).length === 0}>
            {buttonText}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default EntityTable
